import { useCallback, useMemo, useState } from 'react';

import { CreateCustomField } from '../CreateCustomField.type';
import { useCounter } from './useCounter';

interface useApplicationAddExistingStepsProps<
  TForm extends { custom_fields: CreateCustomField[]; id: string },
> {
  newEntities: TForm[];
  allEntities: TForm[];
  customFields: CreateCustomField[];
  onComplete: (entities: TForm[]) => void;
  updateEntity: (entity: TForm) => void;
  removeEntity: (entityId: string) => void;
}

export type ApplicationAddExistingStep = 'select' | 'edit';

export const useApplicationAddExistingSteps = <
  TForm extends { custom_fields: CreateCustomField[]; id: string },
>({
  newEntities,
  allEntities,
  customFields,
  onComplete,
  updateEntity,
  removeEntity,
}: useApplicationAddExistingStepsProps<TForm>) => {
  const [step, setStep] = useState<ApplicationAddExistingStep>(
    newEntities.length > 0 ? 'edit' : 'select',
  );

  const {
    increment: next,
    hasNext,
    value: currentIndex,
  } = useCounter({ max: newEntities.length - 1 });

  const currentEntity = newEntities[currentIndex];

  const hasCustomFields = useMemo(() => {
    return newEntities.some((e) => e.custom_fields.length > 0) || customFields.length > 0;
  }, [newEntities, customFields.length]);

  const onConfirmSelection = useCallback(() => {
    if (!newEntities.length || !hasCustomFields) {
      onComplete(allEntities);
      return;
    }

    setStep('edit');
  }, [allEntities, hasCustomFields, newEntities.length, onComplete]);

  const onUpdateEntity = useCallback(
    (entity: TForm) => {
      updateEntity(entity);

      if (hasNext) {
        next();
        return;
      }

      const updatedEntities = allEntities.map((e) => (e.id === entity.id ? entity : e));
      onComplete(updatedEntities);
    },
    [updateEntity, hasNext, allEntities, onComplete, next],
  );

  const onCloseEntityUpdate = useCallback(
    (entityId: string) => {
      if (hasNext) {
        removeEntity(entityId);
        return;
      }

      const updated = allEntities.filter((e) => e.id !== entityId);
      onComplete(updated);
    },
    [hasNext, allEntities, onComplete, removeEntity],
  );

  return {
    step,
    currentEntity,
    hasNext,
    onConfirmSelection,
    onUpdateEntity,
    onCloseEntityUpdate,
  };
};
